import React, { useEffect, useRef, useState, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Classes from './Product.module.css';
import Rating from '@mui/material/Rating';
import { AnimatePresence, motion } from 'framer-motion';
import { CartContext } from '../../Context/CartContext';
import { getProductById } from '../../data/products';
import { useSnackbar } from "notistack";

import Masonry from 'react-masonry-css';
import ProductItem from './ProductItem';
import Faq from '../Home/Faq/Faq';
import SectionCards from '../Home/SectionCards/SectionCards';
import { Helmet } from 'react-helmet-async';

const breakpointColumnsObj = {
    default: 4,
    1100: 3,
    700: 2,
    500: 1
};

const LoadingSpinner = () => (
    <div class="loadingio-spinner-eclipse-nq4q5u6dq7r">
        <div class="ldio-x2uulkbinbj">
            <div></div>
        </div>
    </div>
)

export default function Product({ setopenCart }) {
    const { id } = useParams();
    const { addToCart, cartItems } = useContext(CartContext);
    const [product, setProduct] = useState(null);
    const [selectedSize, setSelectedSize] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [activeImage, setActiveImage] = useState(0);
    const [imageGallery, setImageGallery] = useState([]);
    const [sizes, setSizes] = useState(null);

    const { enqueueSnackbar } = useSnackbar();
    // Available sizes data
    const [mainImageLoaded, setMainImageLoaded] = useState(false);

    useEffect(() => {
        const productData = getProductById(parseInt(id));
        setProduct(productData);
        setImageGallery(productData?.images || []);
        setSizes(productData?.size || []);
        if (productData) {
            setActiveImage(0);
        }
    }, [id]);

    const handleSizeChange = (e) => {
        setSelectedSize(e.target.value);
    };

    const handleAddToCart = () => {
        if (!selectedSize) {
            enqueueSnackbar('Please select a size', { variant: 'warning' });
            return;
        }

        addToCart({
            ...product,
            selectedSize,
            quantity,
            selectedImage: imageGallery[activeImage]
        });

        enqueueSnackbar('Item added to cart successfully!', {
            variant: 'success',
            style: {
                backgroundColor: "black", // custom success green
                color: "#fff",
            },
        });
    };

    const navigateTo = useNavigate();

    const handleOrderNow = () => {
        if (!selectedSize) {
            enqueueSnackbar('Please select a size', { variant: 'warning' });
            return;
        }

        // Check if item with same ID and size already exists in cart
        const itemExists = cartItems.some(item =>
            item.id === product.id &&
            item.selectedSize === selectedSize
        );

        if (itemExists) {
            if (window.innerWidth < 600) {
                navigateTo('/cart');
            } else {
                setopenCart(true);
            }
            return;
        }

        // Add to cart if not already exists
        addToCart({
            ...product,
            selectedSize,
            quantity,
            selectedImage: imageGallery[activeImage]
        });

        if (window.innerWidth < 600) {
            navigateTo('/cart');
        } else {
            setopenCart(true);
        }

        enqueueSnackbar('Item added to cart successfully!', {
            variant: 'success',
            style: {
                backgroundColor: "black",
                color: "#fff",
            },
        });
    };

    if (!product) {
        return (<>
            <div className={Classes.hero}>
                <LoadingSpinner />
            </div>
        </>)
    }

    return (
        <>
            <Helmet>
                <title>{product?.title} | Luxaywear</title>
                <meta
                    name="description"
                    content={`Shop the best replica ${product?.brand} ${product?.title} at LuxayWear Dubai. High-quality look, unbeatable prices.`}
                />
                <meta
                    name="keywords"
                    content={`replica ${product?.brand}, ${product?.title} Dubai, buy replica shoes online, cheap branded shoes Dubai, LuxayWear, ${product?.brand} dupes UAE`}
                />
                <meta name="robots" content="index, follow" />
                <link rel="canonical" href={`https://luxaywear.com/product/${product?.id}`} />
            </Helmet>

            <section className={Classes.hero}>
                <div className={Classes.grid}>
                    <div className={Classes.imageCon}>
                        <img
                            src={mainImageLoaded ? imageGallery[activeImage] : '/images/placeholder.png'}
                            alt={product?.title}
                            rel="noopener noreferrer"
                            draggable={false}
                            className={Classes.mainImage}
                            onLoad={() => setMainImageLoaded(true)}
                        />
                    </div>
                    <div className={Classes.gridRight}>
                        <h5 className={Classes.title1}>{product?.title}</h5>
                        <h5 className={Classes.title2}>{product?.subTitle}</h5>

                        {/* <div className={Classes.ratingContainer}>
                        <Rating
                            name="product-rating"
                            value={product.rating}
                            precision={0.5}
                            readOnly
                        />
                        <span className={Classes.ratingText}>{product.rating}</span>
                    </div> */}

                        <div className={Classes.priceCon} >
                            <h5 className={Classes.price}>AED {product?.price}</h5>
                            <h5 className={Classes.originalPrice}>{product?.originalPrice}</h5>
                        </div>
                        <h5 className={Classes.colorText} >{product?.images?.length} colors</h5>
                        <div className={Classes.gallery}>
                            {imageGallery?.map((img, index) => (
                                <ProductItem
                                    key={index}
                                    img={img}
                                    index={index}
                                    product={product}
                                    activeImage={activeImage}
                                    setActiveImage={setActiveImage}
                                />
                            ))}
                        </div>

                        <div className={Classes.sizeSelector}>
                            <select
                                value={selectedSize}
                                onChange={handleSizeChange}
                                className={Classes.inputField}
                            >
                                <option value="">Select Size</option>
                                {sizes.map((size) => (
                                    <option key={size} value={size}>
                                        Size {size}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <h5 className={Classes.label} > Quantity </h5>

                        <div className={Classes.flex10}>
                            <button className={Classes.quantityBtn} type='button'
                                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                disabled={quantity <= 1}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width='10px'
                                    cursor='pointer'
                                    fill='#353535'
                                    viewBox="0 0 448 512"
                                >
                                    <path d="M432 256c0 17.7-14.3 32-32 32L48 288c-17.7 0-32-14.3-32-32s14.3-32 32-32l352 0c17.7 0 32 14.3 32 32z" />
                                </svg>
                            </button>
                            <h5>{quantity}</h5>
                            <button className={Classes.quantityBtn} type='button'
                                onClick={() => setQuantity(quantity + 1)}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width='12px'
                                    cursor='pointer'
                                    fill='#353535'
                                    viewBox="0 0 448 512"
                                >
                                    <path d="M256 80c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 144L48 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l144 0 0 144c0 17.7 14.3 32 32 32s32-14.3 32-32l0-144 144 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-144 0 0-144z" />
                                </svg>
                            </button>
                        </div>

                        <button
                            className={Classes.checkoutBtnOutline}
                            onClick={handleAddToCart}
                        >
                            <h5>Add to Bag</h5>
                        </button>
                        <button
                            className={Classes.checkoutBtn}
                            onClick={handleOrderNow}
                        >
                            <h5>Order Now</h5>
                        </button>

                        <div className={Classes.desc}>
                            <h4>Description</h4>
                            <h5>
                                {product.description || `
                            Say hello to the next generation of ${product.brand} technology. 
                            This product features our premium quality materials, creating 
                            a comfortable experience. The ${product.title} combines style 
                            and functionality for everyday wear.
                            `}
                            </h5>
                        </div>

                    </div>
                </div>
                <div className={Classes.reviews} >
                    <div className='flexAlign10' style={{ marginBottom: '1rem' }} >
                        <h4 className={Classes.reviewsTitle} >Reviews ({product?.reviews?.length})</h4>
                        <div className='flexAlign5'>
                            <Rating
                                name="product-rating"
                                value={product?.rating}
                                precision={0.5}
                                readOnly
                                size='small'
                            />
                            <span className={Classes.ratingText}> {product?.rating} </span>
                        </div>
                    </div>
                    <Masonry
                        breakpointCols={breakpointColumnsObj}
                        className="my-masonry-grid"
                        columnClassName="my-masonry-grid_column"
                    >
                        {product?.reviews && product?.reviews.map((review, idx) => (
                            <div key={idx} className={Classes.reviewCard} >
                                <h5 className={Classes.name} > {review.name} </h5>
                                <h5 className={Classes.rating} ></h5>
                                <div className={Classes.ratingContainer}>
                                    <Rating
                                        name="product-rating"
                                        value={review?.rating}
                                        precision={0.5}
                                        readOnly
                                        size='small'
                                    />
                                    <span className={Classes.ratingText}> {review.rating} </span>
                                </div>
                                <div className={Classes.location} >
                                    <h5> Location: </h5>
                                    <h5 className={Classes.locationText} >
                                        {review.location}
                                    </h5>
                                </div>
                                <h5 className={Classes.reviewDesc} >
                                    {review.review}
                                </h5>
                            </div>
                        ))}
                    </Masonry>
                </div>
            </section>
            <SectionCards />
            <Faq />
        </>
    );
}